import {
  Users,
  Shield,
  Sparkles,
  Building2,
  Wallet,
  LayoutGrid,
  ArrowRight,
  Star,
  CheckCircle,
  Check,
  Clock,
  User,
  ShieldCheck,
  ChevronDown,
  Layout,
  Globe,
  Zap,
  Settings,
  FileText,
  Building,
} from 'lucide-react';

export function LandingPage() {
  const isDev = import.meta.env.DEV;
  const protocol = isDev ? 'http' : 'https';
  const port = isDev ? ':3000' : '';
  // In dev: residences.localhost:3000, in prod: residences.unidom.az
  const domain = isDev ? 'residences.localhost' : 'residences.unidom.az';
  const appUrl = `${protocol}://${domain}${port}/`;

  return (
    <div className="bg-stone-50 text-stone-800 antialiased">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Now available nationwide
              </div>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-normal text-stone-900 leading-[1.1]"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                Modern living,{' '}
                <span className="italic text-stone-500">effortlessly managed.</span>
              </h1>
              <p className="text-lg text-stone-600 max-w-lg leading-relaxed">
                The complete property management platform for student residences. Streamline operations, 
                delight residents, and scale your portfolio with confidence.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={appUrl}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-stone-900 text-white font-medium rounded-full hover:bg-stone-800 transition-all hover:scale-105"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href={appUrl}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-stone-900 font-medium rounded-full border border-stone-200 hover:border-stone-300 transition-all"
                >
                  Watch Demo
                </a>
              </div>
              {/* Social Proof */}
              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-stone-200 border-2 border-stone-50 flex items-center justify-center"
                    >
                      <User className="w-5 h-5 text-stone-400" />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />
                    ))}
                  </div>
                  <p className="text-sm text-stone-600">Trusted by 200+ property managers</p>
                </div>
              </div>
            </div>

            {/* Right Column - Dashboard Preview */}
            <div className="relative">
              <div className="relative bg-stone-200 rounded-[2rem] p-6 aspect-[4/3] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-300" />
                {/* Dashboard Mockup */}
                <div className="relative bg-white rounded-2xl shadow-xl p-6 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Building className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-stone-900">Sunset Residences</p>
                        <p className="text-sm text-stone-500">Baku, Azerbaijan</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-stone-200" />
                      <div className="w-3 h-3 rounded-full bg-stone-200" />
                      <div className="w-3 h-3 rounded-full bg-stone-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-sm text-stone-500 mb-1">Occupancy</p>
                      <p className="text-2xl font-semibold text-stone-900">94%</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-sm text-stone-500 mb-1">Revenue</p>
                      <p className="text-2xl font-semibold text-stone-900">₼45K</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-sm text-stone-500 mb-1">Tickets</p>
                      <p className="text-2xl font-semibold text-stone-900">12</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-stone-900">Rent collection complete</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-stone-900">3 maintenance requests pending</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">Occupancy Update</p>
                    <p className="text-sm text-stone-500">95% Occupied</p>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full w-[95%] bg-green-500 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Strip */}
      <section className="py-12 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs uppercase tracking-widest text-stone-400 mb-8">
            Trusted by leading property management firms
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {['Deloitte', 'KPMG', 'PwC', 'McKinsey', 'Cushman & Wakefield'].map((brand) => (
              <span
                key={brand}
                className="text-xl font-semibold text-stone-300 hover:text-stone-400 transition-colors"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features - Bento Grid */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-orange-600 font-medium mb-4">
              Our Services
            </p>
            <h2
              className="text-4xl sm:text-5xl font-normal text-stone-900"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Everything you need to <span className="italic text-stone-500">manage smarter</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Building Management - Large Card */}
            <div className="md:col-span-2 group bg-white rounded-[2rem] p-8 border border-stone-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-orange-600" />
                </div>
                <ArrowRight className="w-6 h-6 text-stone-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <h3 className="text-2xl font-semibold text-stone-900 mb-3">Building Management</h3>
              <p className="text-stone-600 leading-relaxed max-w-md">
                Comprehensive tools to manage multiple properties, track occupancy rates, 
                and oversee all aspects of your residential portfolio from a single dashboard.
              </p>
            </div>

            {/* Resident Portal */}
            <div className="group bg-white rounded-[2rem] p-8 border border-stone-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-stone-600" />
                </div>
                <ArrowRight className="w-6 h-6 text-stone-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Resident Portal</h3>
              <p className="text-stone-600 leading-relaxed">
                Self-service portal for residents to pay rent, submit requests, and communicate with management.
              </p>
            </div>

            {/* Maintenance Requests */}
            <div className="group bg-white rounded-[2rem] p-8 border border-stone-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center">
                  <Settings className="w-7 h-7 text-stone-600" />
                </div>
                <ArrowRight className="w-6 h-6 text-stone-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Maintenance Requests</h3>
              <p className="text-stone-600 leading-relaxed">
                Streamlined ticketing system for tracking and resolving maintenance issues efficiently.
              </p>
            </div>

            {/* Smart Access Control - Large Card */}
            <div className="md:col-span-2 group bg-white rounded-[2rem] p-8 border border-stone-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center">
                  <Shield className="w-7 h-7 text-orange-600" />
                </div>
                <ArrowRight className="w-6 h-6 text-stone-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <h3 className="text-2xl font-semibold text-stone-900 mb-3">Smart Access Control</h3>
              <p className="text-stone-600 leading-relaxed max-w-md">
                Secure OTP-based entry system with role-based permissions. Manage who has access 
                to what, track entry logs, and ensure resident safety with modern security protocols.
              </p>
            </div>

            {/* Payment Processing */}
            <div className="group bg-white rounded-[2rem] p-8 border border-stone-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center">
                  <Wallet className="w-7 h-7 text-stone-600" />
                </div>
                <ArrowRight className="w-6 h-6 text-stone-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Payment Processing</h3>
              <p className="text-stone-600 leading-relaxed">
                Automated rent collection, invoicing, and financial reporting with multiple payment options.
              </p>
            </div>

            {/* Communication Hub */}
            <div className="group bg-white rounded-[2rem] p-8 border border-stone-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center">
                  <Layout className="w-7 h-7 text-stone-600" />
                </div>
                <ArrowRight className="w-6 h-6 text-stone-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Communication Hub</h3>
              <p className="text-stone-600 leading-relaxed">
                Centralized messaging system for announcements, updates, and resident communications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Capabilities List */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-orange-600 font-medium mb-4">
                Capabilities
              </p>
              <h2
                className="text-4xl sm:text-5xl font-normal text-stone-900 mb-6"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                Built for <span className="italic text-stone-500">scale</span>
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed mb-8">
                From single buildings to nationwide portfolios, UniDom.az adapts to your needs 
                with enterprise-grade features and intuitive design.
              </p>
              <a
                href={appUrl}
                className="inline-flex items-center gap-2 text-orange-600 font-medium hover:gap-3 transition-all"
              >
                Explore all features
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: LayoutGrid, title: 'Multi-Property Dashboard', desc: 'Manage all properties from one view' },
                { icon: FileText, title: 'Automated Reporting', desc: 'Generate insights and analytics' },
                { icon: Globe, title: 'Multi-Language Support', desc: 'English, Azerbaijani, and more' },
                { icon: Zap, title: 'Real-Time Sync', desc: 'Instant updates across all devices' },
                { icon: ShieldCheck, title: 'Bank-Grade Security', desc: 'SOC 2 Type II compliant' },
                { icon: Clock, title: '24/7 Support', desc: 'Dedicated customer success team' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-1">{item.title}</h4>
                    <p className="text-sm text-stone-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-orange-600 font-medium mb-4">
              How It Works
            </p>
            <h2
              className="text-4xl sm:text-5xl font-normal text-stone-900"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Get started in <span className="italic text-stone-500">4 simple steps</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-16 left-[12%] right-[12%] h-0.5 bg-stone-200" />
            {[
              { num: '01', title: 'Onboarding', desc: 'Quick setup call to understand your property needs and configure your account.' },
              { num: '02', title: 'Setup', desc: 'Import your data, configure buildings, units, and resident information.' },
              { num: '03', title: 'Go Live', desc: 'Launch the platform for your team and residents with full training support.' },
              { num: '04', title: 'Scale', desc: 'Expand to more properties and optimize operations with data-driven insights.' },
            ].map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="w-16 h-16 bg-white border-2 border-stone-200 rounded-2xl flex items-center justify-center mx-auto mb-6 relative z-10">
                  <span
                    className="text-2xl font-normal text-stone-900"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    {step.num}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-3">{step.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section - Dark */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-stone-900 rounded-[2.5rem] overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left - Content */}
              <div className="p-12 lg:p-16">
                <p className="text-sm uppercase tracking-widest text-orange-400 font-medium mb-4">
                  Why UniDom.az
                </p>
                <h2
                  className="text-4xl sm:text-5xl font-normal text-white mb-6"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  The platform that <span className="italic text-stone-400">grows with you</span>
                </h2>
                <p className="text-stone-400 leading-relaxed mb-8">
                  We have built UniDom.az from the ground up to address the unique challenges 
                  of student housing management in Azerbaijan and beyond.
                </p>
                <div className="space-y-4">
                  {[
                    'Transparent pricing with no hidden fees',
                    'Dedicated support team available 24/7',
                    'Real-time dashboards and analytics',
                    'Regular feature updates and improvements',
                    'Seamless integrations with your existing tools',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-green-400" />
                      </div>
                      <span className="text-stone-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Right - Interactive Dashboard Preview */}
              <div className="bg-stone-800 p-12 lg:p-16 flex items-center">
                <div className="w-full bg-stone-900 rounded-2xl p-6 border border-stone-700">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-semibold text-white">Property Dashboard</h4>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400">Live</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {/* Occupancy Rate */}
                    <div className="bg-stone-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-stone-400">Occupancy Rate</span>
                        <span className="text-lg font-semibold text-white">94%</span>
                      </div>
                      <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                        <div className="h-full w-[94%] bg-gradient-to-r from-orange-500 to-orange-400 rounded-full" />
                      </div>
                    </div>
                    {/* Maintenance Tickets */}
                    <div className="bg-stone-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-stone-400">Maintenance Tickets</span>
                      </div>
                      <div className="flex gap-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full" />
                          <span className="text-sm text-stone-300">8 Resolved</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-orange-500 rounded-full" />
                          <span className="text-sm text-stone-300">3 Pending</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full" />
                          <span className="text-sm text-stone-300">1 Urgent</span>
                        </div>
                      </div>
                    </div>
                    {/* Rent Collection */}
                    <div className="bg-stone-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-stone-400">Rent Collection</span>
                        <span className="text-lg font-semibold text-white">₼42,500</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-green-400">+12%</span>
                        <span className="text-stone-500">from last month</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Star className="w-8 h-8 text-orange-600" />
          </div>
          <blockquote
            className="text-3xl sm:text-4xl font-normal text-stone-900 leading-relaxed mb-8"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            "UniDom.az has completely transformed how we manage our student residences. 
            The automation features alone have saved us 20+ hours per week, and our 
            residents love the self-service portal."
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="w-14 h-14 bg-stone-200 rounded-full flex items-center justify-center ring-4 ring-orange-100">
              <User className="w-7 h-7 text-stone-400" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-stone-900">Nigar Mammadova</p>
              <p className="text-sm text-stone-500">Property Manager, Baku Student Housing</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-orange-600 font-medium mb-4">
              FAQ
            </p>
            <h2
              className="text-4xl sm:text-5xl font-normal text-stone-900"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Common <span className="italic text-stone-500">questions</span>
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: 'How does the pricing work?',
                a: 'Our pricing is based on the number of units you manage. We offer transparent, per-unit pricing with no hidden fees. Contact our sales team for a custom quote based on your portfolio size.',
              },
              {
                q: 'How long does setup take?',
                a: 'Most properties are up and running within 1-2 weeks. Our onboarding team will guide you through data import, configuration, and training to ensure a smooth transition.',
              },
              {
                q: 'Is my data secure?',
                a: 'Absolutely. We use bank-grade encryption, are SOC 2 Type II compliant, and host all data in secure, redundant data centers. Your data is never shared with third parties.',
              },
              {
                q: 'Can I integrate with other tools?',
                a: 'Yes! UniDom.az integrates with popular accounting software, payment processors, and communication tools. We also offer an API for custom integrations.',
              },
              {
                q: 'Do you offer training and support?',
                a: 'We provide comprehensive onboarding training for your team, ongoing webinars, documentation, and 24/7 customer support via chat, email, and phone.',
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="group bg-white rounded-2xl border border-stone-200 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none hover:bg-stone-50 transition-colors">
                  <span className="font-semibold text-stone-900 pr-8">{faq.q}</span>
                  <ChevronDown className="w-5 h-5 text-stone-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6 text-stone-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-footer CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-[2.5rem] p-12 lg:p-16 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="relative">
              <h2
                className="text-4xl sm:text-5xl font-normal text-white mb-6"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                Ready to transform <span className="italic text-orange-100">your properties?</span>
              </h2>
              <p className="text-lg text-orange-100 mb-8 max-w-2xl mx-auto">
                Join 200+ property managers who have streamlined their operations with UniDom.az. 
                Start your free trial today.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href={appUrl}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href={appUrl}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-orange-600 text-white font-semibold rounded-full border-2 border-orange-400 hover:bg-orange-700 transition-colors"
                >
                  Schedule Demo
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
